import { getGamesByCategory, type Game } from '@lib/content';
import type { Category } from '@lib/content';
import categories from '@content/categories.json';
import { SPECIAL_CATEGORIES, CONTENT_CONFIG } from '../config';

/**
 * Returns a game object with content fields replaced by the translation for the given
 * language. Structural fields (slug, thumbnail, playUrl, categories, …) are never
 * overridden. Falls back to the original English content when no translation exists.
 */
export function getLocalizedGame(game: Game, language: string): Game {
  if (language === 'en' || !game.translations) return game;
  const translation = game.translations[language];
  if (!translation) return game;
  return { ...game, ...translation };
}

/**
 * Calculate the number of games in each category
 * @param categories - Array of category objects with slug property
 * @returns Record mapping category slug to game count
 */
export function calculateCategoryCounts(
  categories: Array<{ slug: string; [key: string]: any }>
): Record<string, number> {
  const counts: Record<string, number> = {};

  categories.forEach((cat) => {
    if (cat && cat.slug) {
      counts[cat.slug] = getGamesByCategory(cat.slug).length;
    }
  });

  return counts;
}

/**
 * Get primary categories (excluding special categories like featured, latest, trending)
 * @returns Array of primary categories
 */
export function getPrimaryCategories(): Category[] {
  return categories.filter((c) => !SPECIAL_CATEGORIES.includes(c.slug as any));
}

/**
 * Get game categories with proper filtering and slicing
 * @param game - Game object
 * @param maxCategories - Maximum number of categories to return
 * @returns Array of category objects
 */
export function getGameCategories(
  game: Game,
  maxCategories: number = CONTENT_CONFIG.MAX_CATEGORY_TAGS
): Category[] {
  return (game.categories || [])
    .map(slug => categories.find(c => c.slug === slug))
    .filter((c): c is Category => c !== undefined)
    .slice(0, maxCategories);
}
